import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { t } from '../../lib/i18n';
import { AdminLayout } from '../../components/admin/AdminLayout';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';
import { toast } from 'sonner';
import {
  Search,
  MoreVertical,
  Archive,
  ArchiveRestore,
  Trash2,
  ChevronLeft,
  ChevronRight,
  Users,
  Eye,
  Download,
} from 'lucide-react';

const PAGE_SIZE = 20;

const AdminCampaignsPage = () => {
  const { language } = useAuth();
  const [campaigns, setCampaigns] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [deleteDialog, setDeleteDialog] = useState(null);
  const [detailCampaign, setDetailCampaign] = useState(null);

  const fetchCampaigns = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get('/api/admin/campaigns', {
        params: { skip: page * PAGE_SIZE, limit: PAGE_SIZE, search },
      });
      setCampaigns(res.data.campaigns);
      setTotal(res.data.total);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [page, search]);

  useEffect(() => {
    fetchCampaigns();
  }, [fetchCampaigns]);

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(0);
    fetchCampaigns();
  };

  const toggleArchive = async (campaignId) => {
    try {
      await axios.patch(`/api/admin/campaigns/${campaignId}`);
      toast.success(t('success_saved', language));
      fetchCampaigns();
    } catch (err) {
      toast.error(err.response?.data?.detail || t('error_generic', language));
    }
  };

  const handleDelete = async () => {
    if (!deleteDialog) return;
    try {
      await axios.delete(`/api/admin/campaigns/${deleteDialog.id}`);
      toast.success(t('success_deleted', language));
      setDeleteDialog(null);
      fetchCampaigns();
    } catch (err) {
      toast.error(err.response?.data?.detail || t('error_generic', language));
    }
  };

  const viewDetail = async (campaignId) => {
    try {
      const res = await axios.get(`/api/admin/campaigns/${campaignId}`);
      setDetailCampaign(res.data);
    } catch (err) {
      console.error(err);
    }
  };

  const handleExport = async () => {
    try {
      const res = await axios.get('/api/admin/export/campaigns', { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = 'campaigns_export.csv';
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(t('error_generic', language));
    }
  };

  const totalPages = Math.ceil(total / PAGE_SIZE);

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <h1 className="text-2xl font-bold font-cinzel">{t('admin_campaigns', language)}</h1>
          <Button variant="outline" onClick={handleExport}>
            <Download className="mr-2 h-4 w-4" />
            {t('admin_export_csv', language)}
          </Button>
        </div>

        <form onSubmit={handleSearch} className="flex gap-2 max-w-md">
          <Input placeholder={t('search', language)} value={search} onChange={(e) => setSearch(e.target.value)} />
          <Button type="submit" size="icon" variant="secondary">
            <Search className="h-4 w-4" />
          </Button>
        </form>

        <Card>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border bg-muted/50">
                    <th className="text-left p-3 font-medium">{t('admin_name', language)}</th>
                    <th className="text-left p-3 font-medium hidden sm:table-cell">{t('admin_campaign_owner', language)}</th>
                    <th className="text-left p-3 font-medium hidden md:table-cell">{t('admin_campaign_members_count', language)}</th>
                    <th className="text-left p-3 font-medium hidden md:table-cell">{t('admin_universe', language)}</th>
                    <th className="text-left p-3 font-medium hidden lg:table-cell">{t('admin_created_at', language)}</th>
                    <th className="p-3 w-10"></th>
                  </tr>
                </thead>
                <tbody>
                  {campaigns.map((c) => (
                    <tr
                      key={c.id}
                      className="border-b border-border hover:bg-muted/30 cursor-pointer"
                      onClick={() => viewDetail(c.id)}
                    >
                      <td className="p-3">
                        <div className="flex items-center gap-2">
                          {c.name}
                          {c.is_archived && <Badge variant="secondary">{t('admin_archived', language)}</Badge>}
                        </div>
                      </td>
                      <td className="p-3 text-muted-foreground hidden sm:table-cell">{c.owner_username}</td>
                      <td className="p-3 hidden md:table-cell">
                        <div className="flex items-center gap-1">
                          <Users className="h-3 w-3" /> {c.members_count}
                        </div>
                      </td>
                      <td className="p-3 text-muted-foreground hidden md:table-cell">{c.universe || '—'}</td>
                      <td className="p-3 text-muted-foreground hidden lg:table-cell">
                        {new Date(c.created_at).toLocaleDateString(language === 'fr' ? 'fr-FR' : 'en-US')}
                      </td>
                      <td className="p-3" onClick={(e) => e.stopPropagation()}>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon" className="h-8 w-8">
                              <MoreVertical className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => viewDetail(c.id)}>
                              <Eye className="mr-2 h-4 w-4" />
                              {t('admin_details', language)}
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => toggleArchive(c.id)}>
                              {c.is_archived ? <ArchiveRestore className="mr-2 h-4 w-4" /> : <Archive className="mr-2 h-4 w-4" />}
                              {c.is_archived ? t('admin_campaign_unarchive', language) : t('admin_campaign_archive', language)}
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem className="text-destructive" onClick={() => setDeleteDialog(c)}>
                              <Trash2 className="mr-2 h-4 w-4" />
                              {t('admin_campaign_delete', language)}
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </td>
                    </tr>
                  ))}
                  {campaigns.length === 0 && !loading && (
                    <tr>
                      <td colSpan={6} className="p-6 text-center text-muted-foreground">
                        {t('no_results', language)}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>

        {totalPages > 1 && (
          <div className="flex items-center justify-between">
            <p className="text-sm text-muted-foreground">{total} {t('admin_count_campaigns', language)}</p>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="icon" disabled={page === 0} onClick={() => setPage(page - 1)}>
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <span className="text-sm">{page + 1} / {totalPages}</span>
              <Button variant="outline" size="icon" disabled={page >= totalPages - 1} onClick={() => setPage(page + 1)}>
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Delete confirmation */}
      <Dialog open={!!deleteDialog} onOpenChange={() => setDeleteDialog(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('admin_campaign_delete', language)}</DialogTitle>
            <DialogDescription>{t('admin_campaign_confirm_delete', language)}</DialogDescription>
          </DialogHeader>
          <p className="text-sm font-medium">{deleteDialog?.name}</p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialog(null)}>{t('cancel', language)}</Button>
            <Button variant="destructive" onClick={handleDelete}>{t('delete', language)}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Campaign detail */}
      <Dialog open={!!detailCampaign} onOpenChange={() => setDetailCampaign(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{detailCampaign?.name}</DialogTitle>
          </DialogHeader>
          {detailCampaign && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-2 text-sm">
                <span className="text-muted-foreground">{t('admin_universe', language)}:</span>
                <span>{detailCampaign.universe || '—'}</span>
                <span className="text-muted-foreground">{t('admin_created_at', language)}:</span>
                <span>{new Date(detailCampaign.created_at).toLocaleDateString(language === 'fr' ? 'fr-FR' : 'en-US')}</span>
                <span className="text-muted-foreground">{t('admin_archived', language)}:</span>
                <span>{detailCampaign.is_archived ? t('admin_yes', language) : t('admin_no', language)}</span>
              </div>

              {/* Members */}
              <div>
                <h3 className="font-semibold mb-2">{t('campaign_members', language)} ({detailCampaign.members?.length || 0})</h3>
                <div className="space-y-1">
                  {detailCampaign.members?.map((m) => (
                    <div key={m.user_id} className="flex items-center justify-between text-sm p-2 rounded bg-muted/50">
                      <span>{m.username}</span>
                      <Badge variant="outline">{m.role}</Badge>
                    </div>
                  ))}
                </div>
              </div>

              {/* Stats */}
              {detailCampaign.stats && (
                <div>
                  <h3 className="font-semibold mb-2">{t('admin_statistics', language)}</h3>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-sm">
                    {Object.entries(detailCampaign.stats).map(([key, val]) => (
                      <div key={key} className="p-2 rounded bg-muted/50 text-center">
                        <p className="text-muted-foreground text-xs capitalize">{key.replace('_', ' ')}</p>
                        <p className="font-bold text-lg">{val}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
};

export default AdminCampaignsPage;
